/** MovingPlatformHandler + PlatformMovePoint, driven by the level doc's MovingPlatformProps. */
import type { MovementVars, MovingPlatformProps } from '../../../model/format.ts';
import { GameBehaviour } from '../game.ts';
import { BodyType, SimGo, type SimBody } from '../world.ts';
import { FIXED_DT, smoothDampV, type V2 } from '../unity.ts';
import { compose, invert, mul } from '../../../model/xform.ts';
import { ActivatorBase, resolveActivators } from './activators.ts';

const enum MoveState {
  Running,
  Waiting,
  Stopped,
  Finished,
}

const enum DeactivationAction {
  Nothing,
  StopImmediately,
  StopAtNextPoint,
  ReturnToPreviousPoint,
  BackToStart,
}

const enum FinishAction {
  Finish,
  Stop,
  Loop,
  ReturnToStart,
}

const SPEEDS = [0, 2, 3, 4, 6, 10, 10, 50, 100];
const ACCS = [0, 0.5, 0.2, 0.2, 0.01];

const mvSpeed = (m: MovementVars) => (m.speedOverride !== 0 ? m.speedOverride : SPEEDS[m.speedEnum] ?? 0);
const mvAcc = (m: MovementVars) => (m.accOverride !== 0 ? m.accOverride : ACCS[m.accEnum] ?? 0);
const mvReturnSpeed = (m: MovementVars) => (m.returnSpeedOverride === 0 ? SPEEDS[m.returnSpeedEnum] ?? 0 : m.returnSpeedOverride);
const mvReturnAcc = (m: MovementVars) => (m.returnAccOverride === 0 ? ACCS[m.returnAccEnum] ?? 0 : m.returnAccOverride);

const EMPTY_VARS: MovementVars = { speedOverride: 0, accOverride: 0, speedEnum: 0, accEnum: 0, returnSpeedOverride: 0, returnAccOverride: 0, returnSpeedEnum: 0, returnAccEnum: 0 };

/** (int)(seconds / Time.fixedDeltaTime) in float32 */
function pauseFramesOf(seconds: number) {
  return Math.trunc(Math.fround(Math.fround(seconds) / Math.fround(FIXED_DT)));
}

/** PlatformMovePoint: an activator that is "on" while the platform sits at it */
export class PlatformMovePoint extends ActivatorBase {
  movementVars: MovementVars = EMPTY_VARS;
  pauseFrames = 0;
  pausePlatformOnArrival = false;
  warpToNextPoint = false;
  override noSave = ['movementVars'];
  override awake() {
    const d = this.data;
    if (!d?.movementVars) return;
    const mv = Object.fromEntries(Object.entries(d.movementVars).map(([k, v]) => [k, Number(v) || 0])) as unknown as MovementVars;
    this.setFrom({ movementVars: mv, pauseOnArrivalForSeconds: Number(d.pauseOnArrivalForSeconds) || 0, pausePlatformOnArrival: String(d.pausePlatformOnArrival) === '1', warpToNextPoint: String(d.warpToNextPoint) === '1' });
  }
  setFrom(p: { movementVars?: MovementVars; pauseOnArrivalForSeconds?: number; pausePlatformOnArrival?: boolean; warpToNextPoint?: boolean } | null) {
    this.movementVars = { ...EMPTY_VARS, ...(p?.movementVars ?? {}) };
    this.pauseFrames = pauseFramesOf(p?.pauseOnArrivalForSeconds ?? 0);
    this.pausePlatformOnArrival = !!p?.pausePlatformOnArrival;
    this.warpToNextPoint = !!p?.warpToNextPoint;
  }
}

export class MovingPlatformHandler extends GameBehaviour {
  /** the "MovingPlatform" child whose rigidbodies get moved */
  contentGo: SimGo | null = null;
  props: MovingPlatformProps | null;
  activators: ActivatorBase[] = [];
  defaultVars: MovementVars = EMPTY_VARS;
  movePoints: PlatformMovePoint[] = [];
  rigidBodies: SimBody[] = [];
  rigidBodyOffsets: V2[] = [];
  moveState = MoveState.Stopped;
  moveDirection = 1;
  currentMoveIndex = 0;
  moveToPointIndex = 0;
  platformPosition: V2 = { x: 0, y: 0 };
  currentVelocity: V2 = { x: 0, y: 0 };
  oldActive = false;
  isActive = false;
  waitCounter = 0;
  warpNextFrame = false;
  override noSave = ['contentGo', 'props', 'activators', 'defaultVars', 'movePoints', 'rigidBodies', 'rigidBodyOffsets'];

  constructor(go: SimGo, props: MovingPlatformProps | null) {
    super(go, props ?? {});
    this.props = props;
  }

  override awake() {
    const p = this.props;
    const content = this.contentGo;
    if (!content) return;
    // In Unity the start point sits on the "MovingPlatform" transform, which has no rigidbody and stays
    // put while its child rigidbodies move. Here the content carries the body, so the start point gets
    // its own stationary GameObject.
    const startGo = new SimGo(this.game, 'MovingPlatform (start)', this.go, mul(invert(this.go.worldXform()), content.worldXform()), `${this.go.key}/start`);
    const start = new PlatformMovePoint(startGo, {});
    start.unityType = 'PlatformMovePoint';
    start.setFrom(p?.start ?? null);
    this.game.addBehaviour(start);
    const pts: PlatformMovePoint[] = [];
    if (!p?.dontUseStartAsMovementPoint) pts.push(start);
    (p?.waypoints ?? []).forEach((w, i) => {
      const world = compose(w.x, w.y, 0);
      const g = new SimGo(this.game, w.name || `MoveTo${i}`, this.go, mul(invert(this.go.worldXform()), world), `${this.go.key}/moveTo:${w.id}`);
      const mp = new PlatformMovePoint(g, {});
      mp.unityType = 'PlatformMovePoint';
      mp.setFrom(w);
      this.game.addBehaviour(mp);
      pts.push(mp);
    });
    this.movePoints = pts;
  }

  override start() {
    const p = this.props;
    const content = this.contentGo;
    if (!content || !this.movePoints.length) {
      this.enabled = false;
      return;
    }
    this.moveState = MoveState.Stopped;
    this.platformPosition = content.position;
    const d = { ...EMPTY_VARS, ...(p?.defaultMovementVars ?? {}) };
    if (d.accOverride === 0 && d.accEnum === 0) d.accEnum = 2;
    if (d.speedOverride === 0 && d.speedEnum === 0) d.speedEnum = 3;
    this.defaultVars = d;
    this.activators = resolveActivators(this, p?.activators ?? []);
    this.rigidBodies = [];
    for (const g of content.descendants()) {
      if (!g.body) continue;
      g.body.managed = true;
      g.body.hasKV = true;
      g.body.resetKV();
      this.rigidBodies.push(g.body);
    }
    this.rigidBodyOffsets = this.rigidBodies.map((b) => ({ x: b.position.x - this.platformPosition.x, y: b.position.y - this.platformPosition.y }));
    if (p?.dontUseStartAsMovementPoint) this.currentMoveIndex = this.movePoints.length - 1;
  }

  override fixedUpdate() {
    this.oldActive = this.isActive;
    this.isActive = this.getActivationStatus();
    const [speed, acc] = this.getMovementVars();
    const activatedThisFrame = !this.oldActive && this.isActive;
    const deactivatedThisFrame = this.oldActive && !this.isActive;
    const deact = (this.props?.deactivationAction ?? 0) as DeactivationAction;

    if (activatedThisFrame) {
      if (this.activators.length) this.sound('platformMove', this.platformPosition);
      if (this.moveState === MoveState.Running && (deact === DeactivationAction.BackToStart || deact === DeactivationAction.ReturnToPreviousPoint)) this.moveDirection *= -1;
      if (this.moveState !== MoveState.Waiting && this.moveState !== MoveState.Finished) {
        this.moveState = MoveState.Running;
        this.moveDirection = 1;
      }
    }
    if (deactivatedThisFrame && this.moveState !== MoveState.Finished && this.moveState !== MoveState.Waiting) {
      switch (deact) {
        case DeactivationAction.BackToStart:
          this.moveDirection = -1;
          this.moveState = MoveState.Running;
          break;
        case DeactivationAction.ReturnToPreviousPoint:
          this.moveDirection *= -1;
          break;
        case DeactivationAction.StopImmediately:
          this.moveState = MoveState.Stopped;
          break;
      }
    }
    if (this.moveState === MoveState.Finished || this.moveState === MoveState.Stopped) return;

    this.moveToPointIndex = this.getMoveToPointIndex();
    const moveToPoint = this.movePoints[this.moveToPointIndex];
    this.movePoints.forEach((m, i) => m.setActiveState(i === this.currentMoveIndex));

    if (this.moveState === MoveState.Running) {
      const target = moveToPoint.go.position;
      if (this.warpNextFrame) this.platformPosition = target;
      else this.platformPosition = smoothDampV(this.platformPosition, target, this.currentVelocity, acc, speed, FIXED_DT);
      this.moveAllBodies(this.platformPosition, this.warpNextFrame, this.warpNextFrame);
      this.warpNextFrame = false;
      if (Math.hypot(this.platformPosition.x - target.x, this.platformPosition.y - target.y) < 0.01) {
        this.moveAllBodies(target, true);
        this.currentVelocity = { x: 0, y: 0 };
        this.currentMoveIndex = this.moveToPointIndex;
        if (moveToPoint.pausePlatformOnArrival) this.moveState = MoveState.Stopped;
        else if (moveToPoint.pauseFrames > 0) {
          this.moveState = MoveState.Waiting;
          this.waitCounter = moveToPoint.pauseFrames;
        } else this.continueToNext(this.isActive, moveToPoint);
      }
    }
    if (this.moveState === MoveState.Waiting) {
      this.waitCounter--;
      if (this.waitCounter <= 0) {
        this.waitCounter = 0;
        this.continueToNext(this.isActive, moveToPoint);
      }
    }
  }

  private continueToNext(isActive: boolean, moveToPoint: PlatformMovePoint) {
    const finish = (this.props?.finishAction ?? 0) as FinishAction;
    const deact = (this.props?.deactivationAction ?? 0) as DeactivationAction;
    const n = this.movePoints.length;
    if (isActive) this.moveState = MoveState.Running;
    let handled = false;
    if (moveToPoint.warpToNextPoint) this.warpNextFrame = true;
    if (!isActive) {
      if (finish === FinishAction.Finish && this.currentMoveIndex === n - 1) this.moveState = MoveState.Finished;
      else {
        switch (deact) {
          case DeactivationAction.BackToStart:
            this.moveState = MoveState.Running;
            this.moveDirection = -1;
            if (this.currentMoveIndex > 0) this.currentMoveIndex--;
            else {
              this.moveState = MoveState.Stopped;
              this.moveDirection = 1;
            }
            handled = true;
            break;
          case DeactivationAction.ReturnToPreviousPoint:
          case DeactivationAction.StopAtNextPoint:
          case DeactivationAction.StopImmediately:
            this.moveState = MoveState.Stopped;
            if (deact === DeactivationAction.ReturnToPreviousPoint) this.moveDirection *= -1;
            break;
          case DeactivationAction.Nothing:
            this.moveState = MoveState.Running;
            break;
        }
      }
    }
    if (handled) return;
    if (this.currentMoveIndex === n - 1 && this.moveDirection === 1) {
      switch (finish) {
        case FinishAction.Loop:
          break;
        case FinishAction.ReturnToStart:
          this.currentMoveIndex--;
          this.moveDirection = -1;
          break;
        case FinishAction.Stop:
          this.moveState = MoveState.Stopped;
          break;
        case FinishAction.Finish:
          this.moveState = MoveState.Finished;
          break;
      }
    } else if (this.moveDirection === -1) {
      if (this.currentMoveIndex === 0) {
        switch (finish) {
          case FinishAction.Loop:
            this.currentMoveIndex = n - 1;
            break;
          case FinishAction.ReturnToStart:
            this.moveDirection = 1;
            if (!isActive) this.moveState = MoveState.Stopped;
            break;
          case FinishAction.Finish:
          case FinishAction.Stop:
            this.moveState = MoveState.Stopped;
            break;
        }
      } else this.currentMoveIndex--;
    }
  }

  private getMoveToPointIndex() {
    return (this.currentMoveIndex + (this.moveDirection === 1 ? 1 : 0)) % this.movePoints.length;
  }

  private getMovementVars(): [number, number] {
    const m = this.movePoints[(this.currentMoveIndex + 1) % this.movePoints.length].movementVars;
    const d = this.defaultVars;
    let speed = mvSpeed(m);
    let acc = mvAcc(m);
    if (this.moveDirection === -1) {
      speed = mvReturnSpeed(m);
      acc = mvAcc(m);
      if (speed === 0) speed = mvReturnSpeed(d);
      if (acc === 0) acc = mvReturnAcc(d);
    }
    if (speed === 0) speed = mvSpeed(d);
    if (acc === 0) acc = mvAcc(d);
    return [speed, acc];
  }

  private getActivationStatus() {
    if (this.activators.length === 0) return true;
    if ((this.props?.activationType ?? 0) === 0) return this.activators.every((a) => a.isActivated);
    if (this.props?.activationType === 1) return this.activators.some((a) => a.isActivated);
    return false;
  }

  private moveAllBodies(pos: V2, clearVelocity = false, warp = false) {
    this.rigidBodies.forEach((b, i) => {
      if (!b.go.comps.has('MovingPlatformIgnore')) {
        const t = { x: pos.x + this.rigidBodyOffsets[i].x, y: pos.y + this.rigidBodyOffsets[i].y };
        if (warp) b.position = t;
        else b.movePosition(t);
      }
      if (clearVelocity && b.type !== BodyType.Static) b.velocity = { x: 0, y: 0 };
    });
  }
}
